import React, { useState } from 'react';
import { AirplaneState, DisplayOptions } from '../types';

// Distinct colors for tracked plane paths
const TRACK_COLORS = [
  '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#a855f7',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
];

export function getTrackColor(index: number): string {
  return TRACK_COLORS[index % TRACK_COLORS.length];
}

interface SidebarProps {
  planeIds: string[];
  displayOptions: DisplayOptions;
  onDisplayOptionsChange: (options: DisplayOptions) => void;
  planeCount: number;
  totalPlanes: number;
  onCenterView: () => void;
  keepInView: boolean;
  onKeepInViewChange: (value: boolean) => void;
  trackedPlaneIds: string[];
  onTrackedPlanesChange: (ids: string[]) => void;
  followPlaneId: string | null;
  onFollowPlane: (id: string | null) => void;
  airplanes: AirplaneState[];
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const altitudeRanges = [
  { label: 'Low', color: '#3CAEA3', range: '< 5,000 ft' },
  { label: 'Medium', color: '#F6D55C', range: '5k–15k ft' },
  { label: 'High', color: '#ED553B', range: '15k–25k ft' },
  { label: 'Very High', color: '#9370DB', range: '> 25,000 ft' },
];

const Sidebar: React.FC<SidebarProps> = ({
  planeIds,
  displayOptions,
  onDisplayOptionsChange,
  planeCount,
  totalPlanes,
  trackedPlaneIds,
  onTrackedPlanesChange,
  followPlaneId,
  onFollowPlane,
  airplanes,
  mobileOpen,
  onMobileClose,
}) => {
  const [isOpen, setIsOpen] = useState(true); // desktop only
  const [trackSearch, setTrackSearch] = useState('');
  const [showGuide, setShowGuide] = useState(() => !localStorage.getItem('flightpulse-guided'));

  const addTrackedPlane = (planeId: string) => {
    onTrackedPlanesChange([planeId]);
    onFollowPlane(planeId);
    if (!displayOptions.showFlightPaths) {
      onDisplayOptionsChange({ ...displayOptions, showFlightPaths: true });
    }
    setTrackSearch('');
    onMobileClose();
  };

  const removeTrackedPlane = (planeId: string) => {
    onTrackedPlanesChange(trackedPlaneIds.filter(id => id !== planeId));
    if (followPlaneId === planeId) onFollowPlane(null);
  };

  const clearAllTracked = () => {
    onTrackedPlanesChange([]);
    onFollowPlane(null);
  };

  const toggleFollow = (planeId: string) => {
    onFollowPlane(followPlaneId === planeId ? null : planeId);
    onMobileClose();
  };

  const trackFilteredIds = planeIds.filter(id =>
    String(id).toLowerCase().includes(trackSearch.toLowerCase()) &&
    !trackedPlaneIds.includes(id)
  );

  const getPlaneAltitude = (id: string): string => {
    const plane = airplanes.find(a => String(a.airplaneId) === id);
    if (!plane) return '—';
    if (plane.altitude === 'ground' || plane.altitude === 0) return 'GND';
    return `${Math.round(Number(plane.altitude) / 1000)}k ft`;
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="sidebar-backdrop" onClick={onMobileClose} />
      )}

      <div className={`sidebar ${isOpen ? 'open' : 'collapsed'} ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Desktop toggle */}
        <div
          className="sidebar-toggle"
          onClick={() => setIsOpen(!isOpen)}
        >
          <svg className="sidebar-toggle-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {isOpen ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
          </svg>
        </div>

        {/* Mobile close button */}
        <div className="sidebar-mobile-header">
          <span className="sidebar-mobile-title">FlightPulse</span>
          <button className="sidebar-mobile-close" onClick={onMobileClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="sidebar-content" style={{ display: isOpen || mobileOpen ? undefined : 'none' }}>
          {/* Onboarding guide — shown once */}
          {showGuide && (
            <div className="sidebar-guide">
              <div className="guide-header">
                <span className="guide-title">How to use</span>
                <button className="guide-dismiss" onClick={() => { setShowGuide(false); localStorage.setItem('flightpulse-guided', '1'); }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="guide-steps">
                <div className="guide-step">
                  <span className="guide-num">1</span>
                  <span>Tap any plane on the map to track it</span>
                </div>
                <div className="guide-step">
                  <span className="guide-num">2</span>
                  <span>Tap a tracked plane below to follow it</span>
                </div>
                <div className="guide-step">
                  <span className="guide-num">3</span>
                  <span>Use the play bar to animate flight paths</span>
                </div>
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="sidebar-stats">
            <div className="sidebar-stat">
              <span className="sidebar-stat-value accent">{planeCount.toLocaleString()}</span>
              <span className="sidebar-stat-label">visible</span>
            </div>
            <div className="sidebar-stat-divider" />
            <div className="sidebar-stat">
              <span className="sidebar-stat-value">{totalPlanes.toLocaleString()}</span>
              <span className="sidebar-stat-label">total</span>
            </div>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search aircraft ID..."
            value={trackSearch}
            onChange={(e) => setTrackSearch(e.target.value)}
            className="sidebar-search"
          />
          {trackSearch && (
            <div className="search-results">
              {trackFilteredIds.length === 0 ? (
                <div className="search-empty">No matching aircraft</div>
              ) : (
                trackFilteredIds.slice(0, 5).map(id => (
                  <div key={id} className="search-result" onClick={() => addTrackedPlane(id)}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    <span>{id}</span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Tracked planes */}
          {trackedPlaneIds.length > 0 ? (
            <div className="tracked-planes">
              <div className="tracked-header">
                <span className="tracked-label">Tracking {trackedPlaneIds.length}</span>
                <button className="tracked-clear" onClick={clearAllTracked}>Clear all</button>
              </div>
              <div className="tracked-list">
                {trackedPlaneIds.map((id, index) => (
                  <div
                    key={id}
                    className={`tracked-item ${followPlaneId === id ? 'following' : ''}`}
                    onClick={() => toggleFollow(id)}
                  >
                    <div className="tracked-item-left">
                      <div className="tracked-color-dot" style={{ backgroundColor: getTrackColor(index) }} />
                      <span className="tracked-item-id">{id}</span>
                      <span className="tracked-item-alt">{getPlaneAltitude(id)}</span>
                    </div>
                    <div className="tracked-item-right">
                      {followPlaneId === id && (
                        <span className="tracked-following-badge">Following</span>
                      )}
                      <button className="chip-remove" onClick={(e) => { e.stopPropagation(); removeTrackedPlane(id); }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="path-option">
                <span>Show flight path</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={displayOptions.showFlightPaths}
                    onChange={() => onDisplayOptionsChange({ ...displayOptions, showFlightPaths: !displayOptions.showFlightPaths })}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
              <div className="path-option">
                <span>Show full path</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={displayOptions.showFullPaths}
                    onChange={() => onDisplayOptionsChange({ ...displayOptions, showFullPaths: !displayOptions.showFullPaths })}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
            </div>
          ) : !trackSearch ? (
            <div className="track-hint">
              Tap any aircraft on the map to track its flight path.
            </div>
          ) : null}

          {/* Altitude legend */}
          <div className="sidebar-legend-inline">
            {altitudeRanges.map((range, i) => (
              <div key={i} className="legend-item-compact">
                <div className="legend-dot" style={{ backgroundColor: range.color }} />
                <span>{range.label}</span>
              </div>
            ))}
          </div>

          {/* Credits */}
          <div className="sidebar-credits">
            <span>Built by <strong>Figment Analytics</strong></span>
            <span className="sidebar-credits-sub">Data: ADS-B Exchange</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
